<script setup lang="ts" generic="TItem, TDetailItem">
import { Grid } from '@progress/kendo-vue-grid'
import '@progress/kendo-theme-default/dist/all.css'
import { ref } from 'vue'
import { Dialog } from '@progress/kendo-vue-dialogs'
import Button from '@/components/ui/button/Button.vue'
import type { GridColumnProps } from '@progress/kendo-vue-grid'

const props = defineProps<{
  columns: GridColumnProps[]
  getData: Function,
  getDetails?: Function,
  formatCell?: Function,
  initialSort: { field: string; dir: 'asc' | 'desc' }
  hasDetail?: boolean
  detailsInitialSort?: { field: string; dir: 'asc' | 'desc' }
  detailColumns?: GridColumnProps[]
  detailDataField?: string
  detailButtonLabel?: string
  dialogTitle?: string
}>()

const emit = defineEmits<{
  (e: 'update:selectedItem', value: TItem | null): void
}>()

const data = ref<TItem[]>([])
const loading = ref(false)
const skip = ref(0)
const take = ref(10)
const total = ref(0)
const sort = ref([props.initialSort])
const showDialog = ref(false)
const selectedItem = ref<TItem | null>(null)
const detailsLoading = ref(false)
const detailsData = ref<TDetailItem[]>([])

const detailSkip = ref(0)
const detailTake = ref(10)
const detailTotal = ref(0)
const detailSort = ref([props.detailsInitialSort || { field: 'id', dir: 'asc' }])

const pageable = {
  buttonCount: 5,
  info: true,
  type: 'numeric',
  pageSizes: [5, 10, 20, 50],
  previousNext: true,
}

const processData = (items: TItem[]) => {
  return items.map((item) => ({
    ...item,
    [props.detailDataField || 'details']: (item as Record<string, unknown>)[props.detailDataField || 'details'] || [],
  }))
}

const pageChangeHandler = async (e: { page: { skip: number; take: number } }) => {
  loading.value = true
  skip.value = e.page.skip
  take.value = e.page.take

  try {
    const response = await props.getData(
      skip.value,
      take.value,
      sort.value[0]?.field,
      sort.value[0]?.dir,
    )
    data.value = processData(response.data)
    total.value = response.total
  } finally {
    loading.value = false
  }
}

const sortChangeHandler = (e: { sort: any[] }) => {
  sort.value = e.sort
  pageChangeHandler({ page: { skip: skip.value, take: take.value } })
}

const openDetails = async (dataItem: TItem) => {
  selectedItem.value = dataItem;
  emit('update:selectedItem', dataItem);
  showDialog.value = true;

  if (props.getDetails) {
    detailsLoading.value = true;
    try {
      console.log(dataItem)
      const response = await props.getDetails(dataItem, detailSkip.value, detailTake.value, detailSort.value[0]?.field, detailSort.value[0]?.dir);
      if (response && response.data.length > 0) {
        detailsData.value = response.data;
        detailTotal.value = response.total;
      } else {
        console.error('Aucune donnée trouvée dans la réponse:', response);
        detailsData.value = [];
      }
    } finally {
      detailsLoading.value = false;
    }
  }
  console.log(detailsData)
}

const closeDialog = () => {
  showDialog.value = false
  selectedItem.value = null
  emit('update:selectedItem', null)
}

const detailPageChangeHandler = (e: { page: { skip: number; take: number } }) => {
  detailSkip.value = e.page.skip
  detailTake.value = e.page.take
  if (selectedItem.value && props.getDetails) {
    openDetails(selectedItem.value)
  }
}

const detailSortChangeHandler = (e: { sort: any[] }) => {
  detailSort.value = e.sort
  if (selectedItem.value && props.getDetails) {
    openDetails(selectedItem.value)
  }
}

const columnsWithDetails = props.hasDetail
  ? [...props.columns, { field: 'actions', title: 'Actions' }]
  : props.columns

pageChangeHandler({ page: { skip: 0, take: 10 } })
</script>

<template>
  <div>
    <Grid
      :data-items="data"
      :columns="columnsWithDetails"
      :loader="loading"
      :sortable="true"
      :sort="sort"
      :pageable="pageable"
      :skip="skip"
      :take="take"
      :total="total"
      @pagechange="pageChangeHandler"
      @sortchange="sortChangeHandler"
      :cell-render="'customCell'"
    >
      <template v-slot:customCell="{ props: cellProps }">
        <td :class="cellProps.class">
          <template v-if="cellProps.field === 'actions' && props.hasDetail">
            <Button
              @click.stop="openDetails(cellProps.dataItem)"
            >
              {{ props.detailButtonLabel || 'Afficher' }}
            </Button>
          </template>
          <template v-else>
            <template v-if="formatCell">
              {{ formatCell(cellProps.field, cellProps.dataItem[cellProps.field]) }}
            </template>
            <template v-else>
              {{ cellProps.dataItem[cellProps.field] }}
            </template>
          </template>
        </td>
      </template>
    </Grid>

    <Dialog v-if="showDialog" @close="closeDialog" :title="dialogTitle" :width="800">
      <Grid
        :data-items="detailsData"
        :columns="detailColumns || []"
        :loader="detailsLoading"
        :sortable="true"
        :sort="detailSort"
        :pageable="pageable"
        :skip="detailSkip"
        :take="detailTake"
        :total="detailTotal"
        @pagechange="detailPageChangeHandler"
        @sortchange="detailSortChangeHandler"
      />
    </Dialog>
  </div>
</template>
